import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useUndeleteAttachmentsApi from 'src/services/useUndeleteAttachmentsApi';

function AttachmentUndelete() {
  const { selectedDocuments, refreshPage, setRefreshCompany, attachmentListData } =
    useInvoiceScanManager();
  const { confirm } = useConfirm();
  const { company } = useCompany();
  const { unDeleteAttachment, isUndeleteAttachmentLoading } = useUndeleteAttachmentsApi();

  const hasSelectedDocuments = selectedDocuments.length > 0;
  const hasUndeletedDocuments = selectedDocuments.some((doc) => !doc.isDeleted);

  const { informationMessageList, errorMessageList, isUndeleteButtonDisabled } = useMemo(() => {
    const isButtonDisabled = !hasSelectedDocuments || hasUndeletedDocuments;
    const informationMessageList: string[] = [];
    const errorMessageList: string[] = [];
    if (attachmentListData.length === 0) {
      informationMessageList.push('You have no record(s) in queue to Undelete.');
    }
    if (!hasSelectedDocuments) {
      informationMessageList.push('Please select record(s) to Undelete.');
    }
    if (hasUndeletedDocuments) {
      errorMessageList.push('Please select only Deleted record(s) to Undelete.');
    }

    return {
      informationMessageList,
      errorMessageList,
      isUndeleteButtonDisabled: isButtonDisabled,
    };
  }, [attachmentListData.length, hasSelectedDocuments, hasUndeletedDocuments]);

  const handleUndeleteInvoice = useCallback(async () => {
    const attachIds = selectedDocuments.map((row) => row.attachId);
    try {
      await unDeleteAttachment({ attachmentIds: attachIds, companyId: Number(company.companyId) });
      toast.success('Record(s) Undeleted Successfully!');
      refreshPage();
    } catch (e) {
      console.error(e);
    }
  }, [company.companyId, refreshPage, selectedDocuments, unDeleteAttachment]);

  const handleUndelete = useCallback(async () => {
    const isUndeleteAccepted = await confirm({
      title: 'Undelete Confirmation',
      content: <div>Are you sure you want to Undelete the selected record(s)?</div>,
      confirmButtonText: 'OK',
    });
    if (isUndeleteAccepted) {
      await handleUndeleteInvoice();
      setRefreshCompany?.((prev) => !prev);
    }
  }, [confirm, handleUndeleteInvoice, setRefreshCompany]);

  return (
    <>
      <PopUp
        content={
          <div className="m-1 max-w-[300px]">
            <PopUpContentWrapper type="Error" contents={errorMessageList} />
            <PopUpContentWrapper type="Information" contents={informationMessageList} />
          </div>
        }
        show={isUndeleteButtonDisabled}
      >
        <Button
          color="secondary"
          onClick={handleUndelete}
          disabled={isUndeleteButtonDisabled}
          loading={isUndeleteAttachmentLoading}
        >
          Undelete
        </Button>
      </PopUp>
    </>
  );
}

export default AttachmentUndelete;
